/**
 * @module app.shared
 */

import { I18N } from "aurelia-i18n";
import { camelCase } from 'aurelia-framework';

export interface ITranslation<T = string> {
    /** Translation resource key */
    key: string | string[];
    /** Override language */
    language?: string;
    /** Count for pluralization */
    count?: number | string;
    /**
     * Context (e.g. `male` or `female`)
     */
    context?: string;
    /** Default value if translation not found */
    default?: T;
    /** Interpolation replacements */
    replace?: object;
    /**
     * Replacement Key for `{{what}}` interpolation
     * */
    what?: string;
    /** Post processor names */
    process?: string | string[]
}

export interface IParsedTranslationKey {
    /** The full translation key */
    full: string,
    /** The namespace of the translation key */
    namespace: string,
    /** The full translation key without the namespace */
    key: string,
    /** The full path of the translation key, split into an array */
    path: string[]
}

export function lookupPluralizationCount(i18n: I18N, count: string | number | null): number | null {
    let countOption: number | null = null;
    if(typeof count === 'number') {
        countOption = count;
    }
    else if(typeof count === 'string') {
        countOption = parseFloat(i18n.tr(`ui:counts.${count}`));
        if(isNaN(countOption)) {
            countOption = null;
        }
    }
    return countOption;
}

/**
 * Translate the information provided by `translation`
 * @param i18n the `I18N` instance
 * @param translation options for translation, `key` property is required
 */
export function translate(i18n: I18N, translation: ITranslation<string>): string {
    const replace: any = { ...{}, ...translation.replace };
    const options: any = {
        lng: translation.language || undefined,
        count: lookupPluralizationCount(i18n, translation.count || null) || undefined,
        context: translation.context || undefined,
        defaultValue: translation.default || undefined,
        replace: replace
    }
    if(translation.what) {
        // we aren't assuming this is a model label, so pass '' to the assumeSuffix parameter
        const whatKeys = getModelLabelTranslationKeys(translation.what, '');
        if(whatKeys.length === 1) {
            // do single call to i18n
            replace.what = `$t(${translation.what})`;
        }
        else if(whatKeys.length > 1) {
            replace.what = i18n.tr(whatKeys, options) || '';
        }
    }
    options.postProcess = translation.process || undefined;
    return i18n.tr(translation.key, options) || '';
}

export function parseTranslationKey(key: string): IParsedTranslationKey {
    const parts = key.split(':', 2);
    let namespace = '';
    let name: string = '';
    if(parts.length == 2) {
        namespace = parts[0];
        name = parts[1];
    }
    else {
        name = parts[0];
    }
    const path = name.split('.');
    return { full: key, namespace, key: name, path };
}

export function buildTranslationKey(namespace: string | null, key: string | string[]): string {
    const parts = typeof key === 'string' ? [key] : key;

    if(namespace && namespace.length > 0) {
        return `${namespace}:${parts.join('.')}`;
    }
    return parts.join('.');
}

export function getModelLabelTranslationKeys(key: string, assumedSuffix: string = '$'): string[] {
    const { namespace, path } = parseTranslationKey(key);
    const keys: string[] = [];
    let target = path[path.length - 1];

    // assume suffix (default '$') if last part doesn't start with '$'
    if(assumedSuffix && !target.startsWith('$')) {
        target = assumedSuffix;
        path.push(target);
    }

    if(target === '$') {
        // value is ns.key.$, look for [ns:key.$]
        keys.push(buildTranslationKey(namespace, path));
    }
    else {
        // value is ns.key.$type, look for [ns:key.$type, ns:key.$]
        keys.push(buildTranslationKey(namespace, path));
        // replace last entry with '$'
        path[path.length - 1] = '$';
        keys.push(buildTranslationKey(namespace, path));
    }

    return keys;
}

/**
 * Find a Model's Action Override Key if one exists
 * @param actionKey action key for translation
 * @param modelKey model key to be translated for interpolation of the `{{what}}` target
 * @example: ('ui:actions.complete', 'cm:carePlanIntervention.$') will return 'cm:carePlanIntervention.$actions.complete' if it exists
*/
export function getModelActionOverrideTranslationKey(actionKey: string, modelKey: string) {
    const action = parseTranslationKey(actionKey);
    const what = parseTranslationKey(modelKey);

    // ('ui:actions.complete', 'cm:carePlanIntervention.$') becomes 'cm:carePlanIntervention.$actions.complete'
    const overrideParts: string[] = [];
    if(what.path[what.path.length - 1].startsWith('$')) {
        overrideParts.push(...what.path.slice(0, what.path.length - 1));
    }
    else {
        overrideParts.push(...what.path);
    }
    overrideParts.push('$' + action.path[0]);
    overrideParts.push(...action.path.slice(1));
    const overrideKey = buildTranslationKey(what.namespace, overrideParts);

    return overrideKey;
}

export function translateModelLabel(i18n: I18N, key: string, count: number | string | null = null): string {
    if(!key) { return ''; }
    const countOption = lookupPluralizationCount(i18n, count);
    const keys = getModelLabelTranslationKeys(key);
    const options = {
        returnEmptyString: true,
        count: countOption === null ? undefined : countOption
    };
    return String(i18n.tr(keys, options) || '');
}

/**
 * Translate an Action Label
 * @param i18n the `I18N` instance
 * @param actionKey action key for translation
 * @param modelKey model key to be translated for interpolation of the `{{what}}` target
 * @param count a resource key or number used to determine whether to use the `plural` context
*/
export function translateActionLabel(i18n: I18N, actionKey: string, modelKey: string | null = null, count: number | string | null = null): string {
    if(!actionKey) { return ''; }
    const countOption = lookupPluralizationCount(i18n, count);

    if(modelKey) {
        const overrideKey = getModelActionOverrideTranslationKey(actionKey, modelKey);
        const modelValue = translateModelLabel(i18n, modelKey, countOption);

        const options = {
            returnEmptyString: true,
            replace: {
                what: modelValue ? modelValue : ' ' // TODO: upgrade i18n and '' should work instead of ' ', or we can use missingInterpolationHandler
            },
            count: countOption === null ? undefined : countOption,
            defaultValue: ''
        };

        return String(i18n.tr([overrideKey, actionKey], options) || '').trim();
    }

    const options = {
        returnEmptyString: true,
        replace: {
            what: ' ', // TODO: upgrade i18n and '' should work instead of ' ', or we can use missingInterpolationHandler
        },
        count: countOption === null ? undefined : countOption,
        defaultValue: ' '
    };

    return String(i18n.tr(actionKey, options) || '');
}

/**
 * Translate an Action Label that interpolates the {{item}} value, where the {{item}} is a singular target of the {{what}} model.
 * @param i18n the `I18N` instance
 * @param actionKey action key for translation
 * @param modelKey model key to be translated for interpolation of the `{{what}}`
 * @param itemValue a string to be interpolated into the `{{item}}`
*/
export function translateActionItemLabel(i18n: I18N, actionKey: string, modelKey: string | null = null, itemValue: string | null = null): string {
    if(!actionKey || !modelKey) { return ''; }
    const countOption = 1;
    let overrideKey: string | null = null;
    let modelValue: string | null = null;

    if(modelKey) {
        overrideKey = getModelActionOverrideTranslationKey(actionKey, modelKey);
        modelValue = translateModelLabel(i18n, modelKey, countOption);
    }

    const options = {
        returnEmptyString: true,
        replace: {
            what: modelValue ? modelValue : ' ', // TODO: upgrade i18n and '' should work instead of ' ', or we can use missingInterpolationHandler
            item: itemValue ? itemValue : ' ', // TODO: upgrade i18n and '' should work instead of ' ', or we can use missingInterpolationHandler
        },
        count: countOption,
        defaultValue: ' '
    };

    if(overrideKey) {
        return String(i18n.tr([overrideKey, actionKey], options) || '').trim();
    } else {
        return String(i18n.tr(actionKey, options) || '').trim();
    }
}

type TranslateListStrategyFunction = (values: string[], delimiter: string, conjunction: string) => string;

interface ITranslateListStrategyLookup {
    readonly [strategy: string]: TranslateListStrategyFunction | undefined
}

function translateListDefault(values: string[], delimiter: string, conjunction: string): string {
    const parts: string[] = [];
    const last = values.length - 1;
    for(let index = 0; index < values.length; index++) {
        const value = values[index];
        if(index > 0) {
            parts.push(index < last ? delimiter : conjunction);
        }
        parts.push(value);
    }
    return parts.join('');
}

function translateListOxford(values: string[], delimiter: string, conjunction: string): string {
    const parts: string[] = [];
    const count = values.length;

    if(count == 2) {
        parts.push(values[0]);
        parts.push(conjunction);
        parts.push(values[1]);
    }
    else {
        const last = count - 1;
        for(let index = 0; index < count; index++) {
            const value = values[index];
            if(index > 0) {
                if(index == last) {
                    if(conjunction.startsWith(' ')) {
                        parts.push(delimiter.trimEnd());
                    }
                    else {
                        parts.push(delimiter);
                    }
                    parts.push(conjunction);
                }
                else {
                    parts.push(delimiter);
                }
            }
            parts.push(value);
        }
    }
    return parts.join('');
}

const translateListStrategies: ITranslateListStrategyLookup = {
    default: translateListDefault,
    oxford: translateListOxford
}

export function translateList(i18n: I18N, values: string[], conjunctionType: string = 'and'): string {
    if(values.length === 0) { return ''; }
    if(values.length === 1) { return values[0]; }

    const strategy = i18n.tr('ui:lists.strategy');
    const delimiter = i18n.tr('ui:lists.delimiter');
    const conjunction = i18n.tr(`ui:lists.conjunctions.${conjunctionType}`) || '';

    const strategyFunction = translateListStrategies[strategy] || translateListStrategies.default;
    if(strategyFunction) {
        return strategyFunction(values, delimiter, conjunction);
    }

    // final fallback, shouldn't happen
    return `(${values.join(', ')})`;
}

export function translateRouteTitle(i18n: I18N, title: string | null): string {
    const defaultTitle = i18n.tr('ui:routes.noTitle') || '';
    if(!title) {
        return defaultTitle;
    }

    const parts = title.split('.');
    if(parts.length < 2) { return title; }

    const module = parts[0];
    const key = `${module}:routes.${parts.slice(1).join('.')}.$title`;
    const options = {
        defaultValue: title
    };

    return String(i18n.tr(key, options) || defaultTitle);
}

export interface IResourceKeyOptions {
    prefix: string,
    suffix: string
};

/**
 * Convert a model code, such as `CD.Member`, to a I18N resource key, like `cd:member`.
 * @param code the Model Code
 * @param options additional options
 */
export function modelCodeToResourceKey(code: string, options: Partial<IResourceKeyOptions> = {}): string {
    if(!code) { return ''; };

    // TODO: put this somewhere as a utility function and maybe a value converter
    const parts = code.split('.');
    const key: string[] = [];
    for(let index = 0; index < parts.length; index++) {
        const part = parts[index];
        // append delimiter
        if(index === 1) {
            key.push(':');
        }
        else if(index > 1) {
            key.push('.');
        }
        // append each part
        if(index === 0) {
            // append namespace (lowercase)
            key.push(part.toLowerCase());
        }
        else {
            if(index === 1) {
                // append prefix
                if(options.prefix !== undefined) {
                    key.push(options.prefix);
                }
            }
            // append part (camel-case)
            key.push(camelCase(part));
        }
    }
    // append suffix
    if(options.suffix !== undefined) {
        key.push('.');
        key.push(options.suffix);
    }
    return key.join('');
}

/**
 * Get a translation key without any qualifiers (parts that starting with a `$` character).
 *
 * Examples:
 * - `cd:member.$` -> `cd:member`
 * - `cm:case.number.$long` -> `cm:case.number`
 * @param key a translation key
 * @returns the base translation key
 */
export function removeTranslationKeyQualifiers(key: string, namespaceDelimiter: string = ':', pathDelimiter: string = '.'): string {
    if(!key) { return ''; }
    const parsed = parseTranslationKey(key);
    const path = parsed.path.filter(part => part && !part.startsWith('$')).join(pathDelimiter);
    return `${parsed.namespace}${namespaceDelimiter}${path}`;
}

export enum NamespaceOptions {
    AE = 'ae',
    AG = 'ag',
    AM = 'am',
    CD = 'cd',
    CM = 'cm',
    Core = 'core',
    CS = 'cs',
    Elli = 'elli',
    Ext = 'ext',
    FM = 'fm',
    HL7 = 'hl7',
    PM = 'pm',
    PP = 'pp',
    UI = 'ui',
    UM = 'um'
}
